/**
 * @fileoverview The bed and the stage a seed-packet review (PacketReview) starts on.
 *
 * The beds and the stages are read as the sheet opens and may not be in on
 * its first render, so the sheet works these out again once they load
 * (unless the gardener has chosen already); with nothing loaded they fall
 * back to a new bed and "Not sure yet".
 *
 * Where: PacketReview, on its first render and again once the beds and
 * stages load.
 * API: none.
 * Watch out: the stage to start on is the one the server marks (packet_start); the Bed list's "new bed" is the string
 * 'new' (NEW_BED), so a real bed's id is always passed as a string too.
 *
 * @module startingChoices
 */

import type { PacketScan } from '@/shared/api/packets';

/** The "new bed" choice in the Bed list. */
export const NEW_BED = 'new';

/**
 * The bed to start on: the one the scan was started from, the one the label
 * named, a new one by the label's name, or their first bed; a new bed when
 * they have none (or none loaded yet). A scan started from Add bed (newFirst)
 * always starts on a new bed, as the owner asked 2026-10-09.
 *
 * @param locationId - The bed the scan was started from, or null.
 * @param scanBed - The bed the packets' label names, as the scan read it.
 * @param beds - Their live beds, in order.
 * @param newFirst - Started from Add bed: a new bed whatever the label says.
 * @returns A bed's id as a string, or NEW_BED.
 */
export function startingBed(
  locationId: number | null,
  scanBed: PacketScan['bed'],
  beds: readonly { id: number }[],
  newFirst = false,
): string {
  if (locationId !== null) return String(locationId);
  if (newFirst) return NEW_BED;
  if (scanBed?.locationId) return String(scanBed.locationId);
  if (scanBed?.isNew || beds.length === 0) return NEW_BED;
  return String(beds[0].id);
}

/**
 * The stage to start on: the one the server marks for packet plants (packet_start, its Planning), else
 * "Not sure yet" (null), as it is before the stages load. The app tests no stage id (C118 F8).
 *
 * @param stages - The stages offered, with the server's packet_start mark.
 * @returns A stage id, or null for "Not sure yet".
 */
export function startingStage(stages: readonly { stage_id: number; packet_start: boolean }[]): number | null {
  return stages.find((stage) => stage.packet_start)?.stage_id ?? null;
}

/**
 * Whether the chosen stage is Planning, by the stage's own flag from the server (C118 F8): the first-to-start line
 * is said only then.
 *
 * @param stages - The stages offered, with the server's planning flag.
 * @param stageId - The chosen stage, or null for "Not sure yet".
 * @returns True when the chosen stage is Planning.
 */
export function atPlanningStage(stages: readonly { stage_id: number; planning: boolean }[], stageId: number | null): boolean {
  return stageId !== null && stages.find((stage) => stage.stage_id === stageId)?.planning === true;
}
